import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import ConfigureIndicatorsModal from '@/pages/parametrizacion/laundries/components/ConfigureIndicatorsModal';
import { updateLaundry } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import './dashboard.css';
import { nf } from './components/SalaControl';
import {
  AvatarAgente,
  AgenteModal,
  EditorIndicacion,
  ESTILO_AGENTE,
  PildorasPrioridad,
  fechaHora,
} from './components/Agentes';
import {
  getAgentes,
  analizarAgente,
  getIndicacionesAgentes,
  getObjetivosAgentes,
  crearObjetivoAgentes,
  actualizarObjetivoAgentes,
  eliminarObjetivoAgentes,
  getMetasCentros,
} from './services/dashboard.services';

const mesActual = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }).slice(0, 7);

const nombreMes = (yyyyMM) => {
  const [y, m] = yyyyMM.split('-').map(Number);
  const t = new Date(y, m - 1, 1).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const AMBITOS = [
  { clave: 'general', nombre: 'Todos los agentes' },
  { clave: 'rrhh', nombre: 'Recursos Humanos' },
  { clave: 'productividad', nombre: 'Productividad' },
  { clave: 'gerencia', nombre: 'Gerencia' },
];
const nombreAmbito = (clave) => AMBITOS.find((a) => a.clave === clave)?.nombre || clave;

const ESTADO_OBJETIVO = {
  en_meta: { sev: 'var(--good)', ink: 'var(--good-ink)', texto: 'En meta' },
  en_riesgo: { sev: 'var(--warning)', ink: 'var(--warn-ink)', texto: 'En riesgo' },
  fuera: { sev: 'var(--critical)', ink: 'var(--critical-ink)', texto: 'Fuera de meta' },
  sin_meta: { sev: 'var(--rule-strong)', ink: 'var(--ink-3)', texto: 'Sin valor de meta' },
  sin_datos: { sev: 'var(--rule-strong)', ink: 'var(--ink-3)', texto: 'Sin datos' },
  libre: { sev: 'var(--s1)', ink: 'var(--s1)', texto: 'Lo evalúa el agente' },
};

// Hook simple de carga con recarga manual.
const useCarga = (fn) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const cargar = useCallback(async () => {
    setError(null);
    try {
      setData(await fn());
    } catch (e) {
      setError(e?.message || 'No se pudo cargar la información');
    }
  }, [fn]);
  useEffect(() => {
    cargar();
  }, [cargar]);
  return { data, setData, error, recargar: cargar };
};

// ---------------------------------------------------------------------------
// Perfil de cada agente
// ---------------------------------------------------------------------------

const TarjetaAgente = ({ agente, configurado, onAnalizado, onAbrir }) => {
  const [analizando, setAnalizando] = useState(false);
  const [error, setError] = useState(null);
  const p = agente.perfil || {};

  const analizar = async () => {
    setAnalizando(true);
    setError(null);
    try {
      await analizarAgente(agente.area);
      onAnalizado();
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalizando(false);
    }
  };

  const ocupado = analizando || agente.analizando;
  return (
    <article className="card agente-perfil" style={{ '--ag': ESTILO_AGENTE[agente.area].color }}>
      <header className="agente-perfil-cab">
        <AvatarAgente area={agente.area} tam={84} analizando={ocupado} />
        <div style={{ minWidth: 0 }}>
          <h3>{agente.nombre}</h3>
          <div className="plan-sub">{p.personaje}</div>
          <p className="agente-personalidad">{p.personalidad}</p>
        </div>
      </header>

      <div className="eyebrow">Funciones</div>
      <ul className="agente-lista">
        {(p.funciones || []).map((f) => (
          <li key={f}>{f}</li>
        ))}
      </ul>

      <details className="agente-detalles">
        <summary>Qué datos ve</summary>
        <ul className="agente-lista">
          {(p.datos || []).map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      </details>
      <details className="agente-detalles">
        <summary>Qué puede consultar al responder preguntas</summary>
        <ul className="agente-lista">
          {(p.herramientas || []).map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      </details>

      <div className="agente-ultimo">
        <div className="eyebrow">Último análisis</div>
        {agente.analisis ? (
          <>
            <div className="plan-sub" style={{ margin: '2px 0 6px' }}>
              {fechaHora(agente.analisis.creado)} · {agente.analisis.origen === 'cron' ? 'automático' : 'manual'} ·{' '}
              {agente.analisis.modelo}
            </div>
            <PildorasPrioridad sugerencias={agente.analisis.sugerencias} />
            <p className="agente-extracto">{agente.analisis.resumen}</p>
          </>
        ) : (
          <div className="plan-sub">Aún no tiene análisis.</div>
        )}
        {(error || agente.ultimoError) && (
          <div className="plan-sub" style={{ color: 'var(--critical-ink)', marginTop: 4 }}>
            {error || `Último intento falló: ${agente.ultimoError.error}`}
          </div>
        )}
      </div>

      <footer className="agente-perfil-pie">
        <button type="button" className="ctl" onClick={onAbrir}>
          Ver sugerencias, historial y chat
        </button>
        <button
          type="button"
          className="ctl"
          onClick={analizar}
          disabled={ocupado || !configurado}
          title={configurado ? 'Analizar con los datos de este momento (≈1-2 min)' : 'Falta configurar la API key'}
        >
          {ocupado ? 'Analizando…' : 'Analizar ahora'}
        </button>
      </footer>
    </article>
  );
};

// ---------------------------------------------------------------------------
// Objetivos del mes
// ---------------------------------------------------------------------------

const OBJETIVO_VACIO = {
  titulo: '',
  detalle: '',
  ambito: 'general',
  indicador: 'horas_extras',
  valorMeta: '',
  centroId: '',
};

const FormularioObjetivo = ({ inicial, indicadores, centros, onGuardar, onCancelar, guardando }) => {
  const [f, setF] = useState(inicial);
  const ind = indicadores.find((i) => i.clave === f.indicador);
  const set = (campo) => (e) => setF((p) => ({ ...p, [campo]: e.target.value }));
  return (
    <form
      className="objetivo-form"
      onSubmit={(e) => {
        e.preventDefault();
        onGuardar(f);
      }}
    >
      <label className="campo ancho">
        <span>Objetivo</span>
        <input value={f.titulo} onChange={set('titulo')} maxLength={160} required placeholder="Ej.: Bajar las horas extras del mes" />
      </label>
      <label className="campo">
        <span>Para</span>
        <select value={f.ambito} onChange={set('ambito')}>
          {AMBITOS.map((a) => (
            <option key={a.clave} value={a.clave}>
              {a.nombre}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span>Indicador</span>
        <select value={f.indicador} onChange={set('indicador')}>
          {indicadores.map((i) => (
            <option key={i.clave} value={i.clave}>
              {i.nombre}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span>Meta{ind?.unidad ? ` (${ind.unidad})` : ''}</span>
        <input type="number" step="any" min="0" value={f.valorMeta} onChange={set('valorMeta')} placeholder={ind?.automatico ? 'Ej.: 800' : 'Opcional'} />
      </label>
      <label className="campo">
        <span>Centro</span>
        <select value={f.centroId} onChange={set('centroId')}>
          <option value="">Toda la operación</option>
          {centros.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
            </option>
          ))}
        </select>
      </label>
      <label className="campo ancho">
        <span>Detalle (opcional)</span>
        <textarea rows={2} value={f.detalle} onChange={set('detalle')} maxLength={1000} placeholder="Contexto para el agente: por qué importa, qué se espera, restricciones…" />
      </label>
      <div className="plan-sub ancho">
        {ind?.automatico
          ? 'El avance se calcula solo con las horas del mes; las horas acumuladas se proyectan al cierre.'
          : 'Sin cálculo automático: el agente evalúa el avance con los datos que ve.'}
      </div>
      <div className="objetivo-form-pie ancho">
        <button type="button" className="ctl" onClick={onCancelar} disabled={guardando}>
          Cancelar
        </button>
        <button type="submit" className="ctl" disabled={guardando || !f.titulo.trim()}>
          {guardando ? 'Guardando…' : 'Guardar objetivo'}
        </button>
      </div>
    </form>
  );
};

const ObjetivosMes = ({ centros }) => {
  const { user } = useAuth();
  const [mes, setMes] = useState(mesActual());
  const cargarObjetivos = useCallback(() => getObjetivosAgentes(mes), [mes]);
  const { data, setData, error } = useCarga(cargarObjetivos);
  const [editando, setEditando] = useState(null); // null | 'nuevo' | objetivo
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const autor = { id: user?._id || user?.id || undefined, nombre: user?.nombre || user?.name || user?.email || undefined };

  const guardar = async (f) => {
    setGuardando(true);
    setMensaje(null);
    const cuerpo = { ...f, mes, centroId: f.centroId || null, actualizadoPor: autor };
    try {
      const r =
        editando === 'nuevo'
          ? await crearObjetivoAgentes(cuerpo)
          : await actualizarObjetivoAgentes(editando.id, cuerpo);
      setData(r);
      setEditando(null);
    } catch (e) {
      setMensaje(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (o) => {
    if (!window.confirm(`¿Eliminar el objetivo "${o.titulo}"?`)) return;
    try {
      setData(await eliminarObjetivoAgentes(o.id));
    } catch (e) {
      setMensaje(e.message);
    }
  };

  return (
    <section aria-labelledby="objetivos-h">
      <div className="sec-head">
        <h2 id="objetivos-h">Objetivos del mes</h2>
        <span className="note">
          Los agentes los reciben en cada análisis y reportan cómo va cada uno
        </span>
        <span className="spacer" />
        <input
          type="month"
          className="ctl"
          aria-label="Mes"
          value={mes}
          onChange={(e) => e.target.value && setMes(e.target.value)}
        />
        <button type="button" className="ctl" onClick={() => setEditando('nuevo')} disabled={!data}>
          + Nuevo objetivo
        </button>
      </div>

      {editando && data && (
        <div className="panel" style={{ padding: 14, marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>
            {editando === 'nuevo' ? `Nuevo objetivo · ${nombreMes(mes)}` : 'Editar objetivo'}
          </h3>
          <FormularioObjetivo
            inicial={
              editando === 'nuevo'
                ? OBJETIVO_VACIO
                : {
                    titulo: editando.titulo,
                    detalle: editando.detalle,
                    ambito: editando.ambito,
                    indicador: editando.indicador,
                    valorMeta: editando.valorMeta ?? '',
                    centroId: editando.centroId || '',
                  }
            }
            indicadores={data.indicadores}
            centros={centros}
            guardando={guardando}
            onGuardar={guardar}
            onCancelar={() => setEditando(null)}
          />
          {mensaje && <div className="burbuja-error" style={{ marginTop: 8 }}>{mensaje}</div>}
        </div>
      )}

      <div className="panel tbl-scroll">
        {error && <div className="empty">{error}</div>}
        {!error && !data && <div className="empty">Cargando…</div>}
        {data && !data.objetivos.length && (
          <div className="empty">
            No hay objetivos para {nombreMes(data.mes)}. Agrega uno para que los agentes lo sigan.
          </div>
        )}
        {data && data.objetivos.length > 0 && (
          <table className="data objetivos-tabla">
            <thead>
              <tr>
                <th>Objetivo</th>
                <th>Para</th>
                <th>Meta</th>
                <th>Avance</th>
                <th>Estado</th>
                <th aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {data.objetivos.map((o) => {
                const estado = o.indicador === 'libre' ? 'libre' : o.avance?.estado || 'sin_datos';
                const e = ESTADO_OBJETIVO[estado] || ESTADO_OBJETIVO.sin_datos;
                return (
                  <tr key={o.id}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{o.titulo}</div>
                      <div className="plan-sub">
                        {o.nombreIndicador} · {o.centro || 'Toda la operación'}
                      </div>
                      {o.detalle && <div className="plan-sub">{o.detalle}</div>}
                    </td>
                    <td>{nombreAmbito(o.ambito)}</td>
                    <td>{o.valorMeta != null ? `${nf(o.valorMeta, 1)} ${o.unidad}` : '—'}</td>
                    <td>
                      {o.avance?.actual != null ? (
                        <>
                          <div>
                            {nf(o.avance.actual, 1)} {o.unidad}
                          </div>
                          {o.avance.proyeccionCierre != null && (
                            <div className="plan-sub">
                              Cierre proyectado: {nf(o.avance.proyeccionCierre, 0)} {o.unidad}
                            </div>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      <span className="pill" style={{ '--sev': e.sev, '--sev-ink': e.ink }}>
                        <i className="dot" />
                        {e.texto}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="ctl" onClick={() => setEditando(o)}>
                        Editar
                      </button>{' '}
                      <button type="button" className="ctl" onClick={() => eliminar(o)}>
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Metas operativas de los centros
// ---------------------------------------------------------------------------

const NOMBRE_RECURSO = {
  aguaFria: 'agua fría',
  aguaCaliente: 'agua caliente',
  gas: 'gas',
  electricidad: 'electricidad',
  detergentes: 'detergentes',
  manoDeObra: 'mano de obra',
};

const valorMeta = (v, unidad) => (v != null ? `${nf(v, 1)}${unidad}` : '—');

// Configuración de indicadores de los centros. Fuente única: la configuración del
// centro (Parametrización → Centros de lavado); se edita con el mismo modal.
const MetasCentros = () => {
  const { toast } = useToast();
  const { data, error, recargar } = useCarga(getMetasCentros);
  const [editando, setEditando] = useState(null);

  const guardar = async (indicadores) => {
    const centro = editando;
    try {
      // Solo el campo indicadores: no reenvía el resto del centro, así no pisa
      // cambios hechos desde otras pantallas.
      const r = await updateLaundry(centro.id, { indicadores });
      if (r?.code !== 200) throw new Error(r?.message || 'No se pudieron guardar los indicadores');
      toast({ title: 'Indicadores guardados', description: centro.nombre });
      setEditando(null);
      recargar();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const sinMetas = (data || []).filter(
    (c) => !c.kgPorHoraAutorizadaMin && !c.porcentajeRechazoMax && !c.pctCiclosAbortadosMax,
  ).length;

  return (
    <section aria-labelledby="metas-h">
      <div className="sec-head">
        <h2 id="metas-h">Metas de los centros</h2>
        <span className="note">
          {data
            ? `De la configuración de indicadores de cada centro · ${sinMetas} de ${data.length} sin metas operativas`
            : 'Cargando…'}
        </span>
      </div>
      <div className="panel tbl-scroll">
        {error && <div className="empty">{error}</div>}
        {data && (
          <table className="data metas-tabla">
            <thead>
              <tr>
                <th>Centro</th>
                <th>Kg/hora mínimo</th>
                <th>% rechazo máximo</th>
                <th>% abortados máximo</th>
                <th>Consumos esperados</th>
                <th>Costo por hora</th>
                <th>Detergente pactado</th>
                <th aria-label="Configurar" />
              </tr>
            </thead>
            <tbody>
              {data.map((c) => {
                const sinMetasCentro =
                  !c.kgPorHoraAutorizadaMin && !c.porcentajeRechazoMax && !c.pctCiclosAbortadosMax;
                const ce = c.consumosEsperados;
                return (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{c.nombre}</div>
                      <div className="plan-sub" style={sinMetasCentro ? { color: 'var(--warn-ink)' } : undefined}>
                        {sinMetasCentro ? 'Sin metas operativas' : c.ciudad || ''}
                      </div>
                    </td>
                    <td>{valorMeta(c.kgPorHoraAutorizadaMin, ' kg/h')}</td>
                    <td>{valorMeta(c.porcentajeRechazoMax, ' %')}</td>
                    <td>{valorMeta(c.pctCiclosAbortadosMax, ' %')}</td>
                    <td
                      title={
                        ce.faltan.length
                          ? `Faltan: ${ce.faltan.map((k) => NOMBRE_RECURSO[k] || k).join(', ')}`
                          : 'Todos configurados'
                      }
                      style={ce.configurados < ce.total ? { color: 'var(--warn-ink)' } : undefined}
                    >
                      {ce.configurados} de {ce.total}
                    </td>
                    <td>{c.costoPorHora ? `$${nf(c.costoPorHora)}` : '—'}</td>
                    <td>{c.detergentePactadoPorKg ? `$${nf(c.detergentePactadoPorKg)}/kg` : '—'}</td>
                    <td>
                      <button type="button" className="ctl" onClick={() => setEditando(c)}>
                        Configurar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="card-sub" style={{ marginTop: 8 }}>
        Estas cifras son la configuración de indicadores de cada centro (la misma de Parametrización →
        Centros de lavado) y son las que usan los agentes para comparar. «Configurar» abre ese mismo
        formulario.
      </p>

      {editando && (
        <ConfigureIndicatorsModal
          isOpen={!!editando}
          laundry={{ _id: editando.id, nombre: editando.nombre, indicadores: editando.indicadores }}
          onClose={() => setEditando(null)}
          onSave={guardar}
        />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

const AgentesPage = () => {
  const agentes = useCarga(getAgentes);
  const indicaciones = useCarga(getIndicacionesAgentes);
  const [abierto, setAbierto] = useState(null);
  const [conversaciones, setConversaciones] = useState({});

  const datos = agentes.data;
  const agenteAbierto = datos?.areas.find((a) => a.area === abierto);
  const centros = indicaciones.data?.centros || [];
  const de = (ambito) => indicaciones.data?.indicaciones.find((i) => i.ambito === ambito);

  return (
    <div className="sala-control">
      <header className="topbar">
        <div className="topbar-in">
          <div className="brand">
            <h1>Agentes IA</h1>
            <div className="sub">
              {datos
                ? `${datos.nombreProveedor} · análisis diario con ${datos.modelo} a las 6:00 a. m. · preguntas con ${datos.modeloPreguntas || datos.modelo}`
                : 'Cargando…'}
            </div>
          </div>
          <div className="controls">
            <Link className="ctl" to="/panel">
              ← Sala de control
            </Link>
          </div>
        </div>
      </header>

      <div className="wrap">
        {datos && !datos.configurado && (
          <div className="plan-aviso" style={{ marginBottom: 12 }}>
            <strong>Agentes no configurados.</strong> Falta <code>{datos.variableApiKey}</code> en{' '}
            <code>empleados-back/.env</code> (ver <code>example.env</code>).
          </div>
        )}

        <section aria-labelledby="perfiles-h">
          <div className="sec-head">
            <h2 id="perfiles-h">Los agentes</h2>
            <span className="note">Personalidad, funciones y último análisis de cada uno</span>
          </div>
          {agentes.error && <div className="panel empty">{agentes.error}</div>}
          {!datos && !agentes.error && <div className="panel empty">Cargando…</div>}
          {datos && (
            <div className="agentes-perfiles">
              {datos.areas.map((a) => (
                <TarjetaAgente
                  key={a.area}
                  agente={a}
                  configurado={datos.configurado}
                  onAnalizado={agentes.recargar}
                  onAbrir={() => setAbierto(a.area)}
                />
              ))}
            </div>
          )}
        </section>

        <ObjetivosMes centros={centros} />

        <section aria-labelledby="indicaciones-h">
          <div className="sec-head">
            <h2 id="indicaciones-h">Indicaciones</h2>
            <span className="note">En qué enfocarse, cómo responder y qué centros vigilar más</span>
          </div>
          {indicaciones.error && <div className="panel empty">{indicaciones.error}</div>}
          {!indicaciones.data && !indicaciones.error && <div className="panel empty">Cargando…</div>}
          {indicaciones.data && (
            <div className="indicaciones-grid">
              {[
                { ambito: 'general', titulo: 'Para todos los agentes', descripcion: 'Las usan los tres agentes.' },
                { ambito: 'rrhh', titulo: 'Para Recursos Humanos', descripcion: 'Solo las usa este agente.' },
                { ambito: 'productividad', titulo: 'Para Productividad', descripcion: 'Solo las usa este agente.' },
                { ambito: 'gerencia', titulo: 'Para Gerencia', descripcion: 'Solo las usa este agente.' },
              ].map((x) => (
                <EditorIndicacion
                  key={x.ambito}
                  titulo={x.titulo}
                  descripcion={x.descripcion}
                  ambito={x.ambito}
                  inicial={de(x.ambito)}
                  centros={centros}
                  maxCaracteres={indicaciones.data.maxCaracteres}
                  onGuardado={indicaciones.setData}
                />
              ))}
            </div>
          )}
        </section>

        <MetasCentros />

        <p className="foot">
          Las sugerencias las genera una IA a partir de los datos del sistema: verifícalas antes de actuar.
          Las personas se envían a la IA con códigos, nunca con nombre ni cédula.
        </p>
      </div>

      {agenteAbierto && (
        <AgenteModal
          agente={agenteAbierto}
          conversacion={conversaciones[agenteAbierto.area] || []}
          setConversacion={(fn) =>
            setConversaciones((prev) => ({
              ...prev,
              [agenteAbierto.area]:
                typeof fn === 'function' ? fn(prev[agenteAbierto.area] || []) : fn,
            }))
          }
          onCerrar={() => setAbierto(null)}
        />
      )}
    </div>
  );
};

export default AgentesPage;
